import React, { useMemo, useState } from "react";
import styled from "styled-components";

import Modal from "../../../components/modal/Modal";
import type { NoteFolderSummary } from "../../../app/services/notes";

/**
 * ページ / フォルダの「移動...」で開く移動先の選択モーダル。
 * セクション直下とセクション内のフォルダツリーから 1 つ選ぶ。
 * フォルダを動かす場合、自分自身と子孫は選べない（excludedFolderIds）。
 */

const INDENT_STEP = 16;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  max-height: min(50dvh, 420px);
  overflow-y: auto;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
`;

const OptionRow = styled.button<{ $depth: number; $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 32px;
  padding: 0 8px 0 ${({ $depth }) => 8 + $depth * INDENT_STEP}px;
  border: none;
  border-bottom: 1px solid var(--soremo-border);
  background: ${({ $selected }) =>
    $selected ? "rgba(0, 154, 206, 0.12)" : "white"};
  color: ${({ $selected }) =>
    $selected ? "var(--soremo-blue)" : "var(--soremo-gray)"};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:disabled {
    color: var(--soremo-border);
    cursor: not-allowed;
  }

  .material-symbols-rounded {
    font-size: 16px;
    flex-shrink: 0;
  }

  .option-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: ${({ $selected }) =>
        $selected ? "rgba(0, 154, 206, 0.12)" : "var(--soremo-background)"};
    }
  }
`;

interface FolderOption {
  folder: NoteFolderSummary;
  depth: number;
}

/** parent_id / sort_order からツリー順（深さ優先）に並べる */
const flattenFolderTree = (folders: NoteFolderSummary[]): FolderOption[] => {
  const ids = new Set(folders.map((f) => f.id));
  const byParent = new Map<string | null, NoteFolderSummary[]>();
  folders.forEach((f) => {
    const parent = f.parent_id && ids.has(f.parent_id) ? f.parent_id : null;
    const list = byParent.get(parent) ?? [];
    list.push(f);
    byParent.set(parent, list);
  });
  const result: FolderOption[] = [];
  const walk = (parent: string | null, depth: number) => {
    const children = [...(byParent.get(parent) ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order,
    );
    children.forEach((folder) => {
      result.push({ folder, depth });
      walk(folder.id, depth + 1);
    });
  };
  walk(null, 0);
  return result;
};

export interface NoteMoveModalProps {
  /** 移動するもの（タイトル表示用） */
  targetLabel: string;
  folders: NoteFolderSummary[];
  /** 今いる場所（null = セクション直下）。同じ場所は選んでも移動しない */
  currentFolderId: string | null;
  /** 選べないフォルダ（フォルダ移動時の自分自身と子孫） */
  excludedFolderIds?: Set<string>;
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onMove: (folderId: string | null) => void;
  onClose: () => void;
}

const NoteMoveModal: React.FC<NoteMoveModalProps> = ({
  targetLabel,
  folders,
  currentFolderId,
  excludedFolderIds,
  loading = false,
  onMove,
  onClose,
}) => {
  const options = useMemo(() => flattenFolderTree(folders), [folders]);
  // undefined = 未選択（null はセクション直下を選んだ状態）
  const [selected, setSelected] = useState<string | null | undefined>(
    undefined,
  );
  const canMove = selected !== undefined && selected !== currentFolderId;

  return (
    <Modal
      title={`「${targetLabel}」の移動先`}
      titleSize="compact"
      size="default"
      onClose={onClose}
      primaryAction={{
        label: "移動",
        onClick: () => {
          if (canMove) onMove(selected ?? null);
        },
        disabled: !canMove,
        loading,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        disabled: loading,
      }}
    >
      <OptionList role="listbox" aria-label="移動先">
        <OptionRow
          type="button"
          role="option"
          aria-selected={selected === null}
          $depth={0}
          $selected={selected === null}
          onClick={() => setSelected(null)}
        >
          <span className="material-symbols-rounded">home</span>
          <span className="option-label bodytext-13">セクション直下</span>
          {currentFolderId === null && (
            <span className="bodytext-11 u-text-light-gray">現在の場所</span>
          )}
        </OptionRow>
        {options.map(({ folder, depth }) => {
          const excluded = excludedFolderIds?.has(folder.id) ?? false;
          return (
            <OptionRow
              key={folder.id}
              type="button"
              role="option"
              aria-selected={selected === folder.id}
              $depth={depth + 1}
              $selected={selected === folder.id}
              disabled={excluded}
              onClick={() => setSelected(folder.id)}
            >
              <span className="material-symbols-rounded">folder</span>
              <span className="option-label bodytext-13">{folder.title}</span>
              {currentFolderId === folder.id && (
                <span className="bodytext-11 u-text-light-gray">
                  現在の場所
                </span>
              )}
            </OptionRow>
          );
        })}
      </OptionList>
    </Modal>
  );
};

export default NoteMoveModal;
